/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Landing page constants — section titles, card data, and promo content
 */

export const WELCOME_BANNER = {
  promoLinkHref: '/support'
} as const;

export const SECTION_TITLES = {
  quickActions: 'QUICK ACTIONS',
  recentsFeatured: 'FEATURED APPS',
  dueThisWeek: "What's Due This Week",
  testimonials: 'TESTIMONIALS'
} as const;

export interface QuickLinkItem {
  label: string;
  href: string;
  external?: boolean;
  tooltip?: string;
  /** LDAP groups allowed to see this link. Omit = visible to all. Mirrors the route's accessibleTo. */
  accessibleTo?: string[];
  /** LDAP groups that gain visibility only in non-prod stages (dev/alpha). Folded into the allow-list when IS_NON_PROD. */
  nonProdOnlyAccessibleTo?: string[];
  /** Hides the link on prod only (alpha/beta keep it). Mirrors the route's environmentRestriction: 'non-prod'. */
  hiddenInProd?: boolean;
}

export interface QuickLinksSection {
  title: string;
  links: QuickLinkItem[];
  external?: boolean;
}

export const QUICK_LINKS_SECTIONS: QuickLinksSection[] = [
  {
    title: 'EXPLORE FINSUITE',
    links: [
      { label: 'All Apps', href: '/all-apps' },
      { label: 'Product Launches', href: '/announcements' }
    ]
  },
  {
    title: 'ORG TOOLS',
    links: [
      { label: 'Frame', href: '/kingpin/goals', tooltip: 'Kingpin Goal Reporting Tool' },
      { label: 'Huddle+', href: '/ea-huddle' },
      { label: 'Baseball Cards', href: '/baseball-cards' },
      { label: 'MBR & Roadmap Docs', href: '/docs' },
      { label: 'MCP Servers', href: '/mcp' },
      {
        label: 'Canary',
        href: '/canary',
        tooltip: 'Connection intelligence for leaders (Connections scorecards)',
        // Mirrors eLDAPGroup in src/constants/AppConstants (inlined to keep this a zero-import
        // constants module; referencing the enum here caused undefined-at-eval under jest mocks).
        accessibleTo: ['das-canary-scorecard-powerusers', 'das-canary-scorecard-viewers'],
        // DEV sees the link only in non-prod (dev/alpha); hidden in beta/prod to match the route guard.
        nonProdOnlyAccessibleTo: ['das-finsuite-application-dev']
      },
      {
        label: 'Airspace',
        href: '/capacity-planner',
        tooltip: 'Weekly Capacity Allocation by Person and Project'
      },
      { label: 'Operational Excellence', href: '/operational-excellence' },
      { label: 'Wellness Tracker', href: '/org-tools/wellness-tracker', tooltip: 'DaS FinTech Summer Wellness Leaderboard' },
      {
        label: 'Talent Surfacing 2.0',
        href: '/operational-excellence/talent-surfacing',
        tooltip: 'Retain high performers by surfacing internal opportunities first',
        // Proposal-stage feature, dev/alpha only and gated to DEV_LDAP — mirrors the route's
        // accessibleTo: DEV_LDAP_GROUPS + environmentRestriction: 'non-prod' (same pattern as Canary/DEV).
        nonProdOnlyAccessibleTo: ['das-finsuite-application-dev'],
        hiddenInProd: true
      }
    ]
  }
];
